import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { PERM, type AuthUser, type Permission } from '@kitobxonlik/contracts';
import { meApi } from '@/api';
import { authApi } from '@/api/auth.api';
import { ApiRequestError } from '@/api/client';

/**
 * Joriy foydalanuvchi.
 *
 * Token httpOnly cookie'da — JS uni o'qiy olmaydi, shuning uchun "kirganmi
 * yoki yo'q" degan savolga faqat `GET /employee/v1/me` javob beradi.
 *
 * RUXSAT vs ROL: UI hech qayerda rol nomini tekshirmaydi (`isAdmin` YO'Q),
 * faqat ruxsatni — `can(PERM.BOOK_WRITE)`. Shunda admin rolga ruxsat
 * qo'shsa/olib tashlasa UI o'zi moslashadi, kod o'zgarmaydi.
 */
export const useSessionStore = defineStore('session', () => {
  const me = ref<AuthUser | null>(null);
  const initialized = ref(false);
  const loading = ref(false);
  /**
   * HR'dan o'tish rad etilgan sabab (server xabari) — `/no-session` ko'rsatadi.
   * `null` — sabab yo'q yoki havola eskirgan (sahifaning o'z matni bor).
   */
  const entryError = ref<string | null>(null);

  const isAuthenticated = computed(() => me.value !== null);
  const roles = computed(() => me.value?.roles ?? []);
  const permissions = computed(() => me.value?.permissions ?? []);
  const orgId = computed(() => me.value?.org_id ?? 0);

  /** Kamida bitta ruxsat bormi (OR) — backend guard'i bilan bir xil qoida */
  const can = (...wanted: Permission[]) =>
    wanted.some((permission) => permissions.value.includes(permission));

  /** Xodim sahifalari (Asosiy, Katalog, Imtihonlarim) ko'rinadimi */
  const isStaff = computed(() => can(PERM.LIBRARY_READ));

  /** Boshqaruv bo'limiga kirish huquqi bormi (istalgan boshqaruv ekrani) */
  const canAdmin = computed(() =>
    can(
      PERM.BOOK_READ,
      PERM.EXAM_READ,
      PERM.STATISTICS_READ,
      PERM.LOG_READ,
      PERM.DICTIONARY_READ,
      PERM.ORGANIZATION_READ,
      PERM.ROLE_READ,
      PERM.USER_READ,
    ),
  );

  function apply(user: AuthUser): void {
    me.value = user;
    entryError.value = null;
    initialized.value = true;
  }

  /** Sahifa yuklanganda: cookie bor bo'lsa foydalanuvchini tiklaydi */
  async function restore(): Promise<void> {
    if (initialized.value || loading.value) return;
    loading.value = true;
    try {
      me.value = await meApi.get();
    } catch (err) {
      me.value = null;
      if (!(err instanceof ApiRequestError && (err.status === 401 || err.status === 403))) {
        console.error('session restore failed', err);
      }
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  /** OneID callback: `code` -> sessiya */
  async function loginWithOneId(
    code: string,
    verifier: string,
    redirectUri: string,
  ): Promise<void> {
    apply((await authApi.oneId(code, verifier, redirectUri)).user);
  }

  /** HR portalidan o'tish: shifrlangan `?p=` -> sessiya */
  async function loginWithHrEntry(p: string): Promise<void> {
    apply((await authApi.hrEntry(p)).user);
  }

  /** Dev: SSO'siz kirish (backend lokal bo'lmasa 404 qaytaradi) */
  async function loginAsDev(pinfl: string): Promise<void> {
    apply((await authApi.devLogin(pinfl)).user);
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout();
    } finally {
      clear();
    }
  }

  /** Sessiya yo'q deb belgilash (401 dan keyin) — qayta so'rov YUBORMAYDI */
  function clear(): void {
    me.value = null;
    initialized.value = true;
  }

  /**
   * Foydalanuvchi ma'lumotini MAJBURAN qayta o'qish.
   *
   * `clear()` dan farqi: u "sessiya yo'q" deb belgilaydi va `restore()` ni
   * ham to'xtatib qo'yadi (`initialized = true`). Rollar/ruxsatlar o'zgarganda
   * esa bizga aynan qayta o'qish kerak — shuning uchun alohida funksiya.
   */
  async function reload(): Promise<void> {
    initialized.value = false;
    await restore();
  }

  return {
    me,
    initialized,
    loading,
    entryError,
    isAuthenticated,
    roles,
    permissions,
    can,
    isStaff,
    canAdmin,
    orgId,
    restore,
    reload,
    loginWithOneId,
    loginWithHrEntry,
    loginAsDev,
    logout,
    clear,
  };
});
